import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, Field, Input, Modal, Select, Spinner, useToast } from "@aigntx/ui";
import { endpoints } from "../api";
import type {
  Catalog,
  Phase,
  ProjectDetail,
  ProjectGroup,
  ProjectMember,
  Stakeholder,
  UserListItem,
} from "../types";
import { human, statusTone } from "./ProjectsPage";

const PHASE_NEXT: Record<string, string> = { pending: "active", active: "done", done: "pending" };

export function ProjectDetailPage() {
  const { id = "" } = useParams();
  const { t } = useTranslation();
  const toast = useToast();
  const [proj, setProj] = useState<ProjectDetail | null>(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [groups, setGroups] = useState<ProjectGroup[]>([]);
  const [error, setError] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [memberDialog, setMemberDialog] = useState<{ mode: "add" | "edit"; target?: ProjectMember } | null>(null);
  const [groupDialog, setGroupDialog] = useState<{ mode: "add" | "edit"; target?: ProjectGroup } | null>(null);

  useEffect(() => {
    Promise.all([
      endpoints.getProject(id),
      endpoints.getCatalog(),
      endpoints.listMembers(id),
      endpoints.listGroups(id),
    ])
      .then(([p, c, m, g]) => {
        setProj(p);
        setCatalog(c);
        setMembers(m);
        setGroups(g);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [id]);

  const canEdit = proj?.myAbilities.includes("edit") ?? false;
  const canManageTeam = proj?.myAbilities.includes("manage_team") ?? false;

  async function cyclePhase(ph: Phase) {
    const next = PHASE_NEXT[ph.status] ?? "pending";
    const updated = await endpoints.updatePhase(id, ph.id, { status: next });
    setProj((p) => (p ? { ...p, phases: p.phases.map((x) => (x.id === ph.id ? updated : x)) } : p));
  }

  async function removeStake(s: Stakeholder) {
    await endpoints.removeStakeholder(id, s.id);
    setProj((p) =>
      p ? { ...p, stakeholders: p.stakeholders.filter((x) => x.id !== s.id), stakeholderCount: p.stakeholderCount - 1 } : p,
    );
  }

  function onStakeAdded(s: Stakeholder) {
    setAddOpen(false);
    setProj((p) => (p ? { ...p, stakeholders: [...p.stakeholders, s], stakeholderCount: p.stakeholderCount + 1 } : p));
  }

  /** Group edits cascade to their members server-side, so refetch both lists. */
  async function reloadTeam() {
    const [m, g] = await Promise.all([endpoints.listMembers(id), endpoints.listGroups(id)]);
    setMembers(m);
    setGroups(g);
  }

  function onMemberSaved() {
    setMemberDialog(null);
    void reloadTeam();
  }

  async function removeMember(m: ProjectMember) {
    await endpoints.removeMember(id, m.id);
    setMembers((ms) => ms.filter((x) => x.id !== m.id));
    setGroups((gs) =>
      gs.map((g) => (g.id === m.groupId ? { ...g, memberCount: Math.max(0, g.memberCount - 1) } : g)),
    );
    toast.success(t("team.removed"));
  }

  function onGroupSaved() {
    setGroupDialog(null);
    void reloadTeam();
  }

  async function removeGroup(g: ProjectGroup) {
    await endpoints.deleteGroup(id, g.id);
    await reloadTeam();
    toast.success(t("group.removed"));
  }

  if (error)
    return (
      <div className="page">
        <Card>{error}</Card>
      </div>
    );
  if (!proj || !catalog)
    return (
      <div className="page center">
        <Spinner />
      </div>
    );

  return (
    <div className="page">
      <div className="crumb">
        <Link to="/projects">{t("projects.title")}</Link> <span className="muted">/ {proj.name}</span>
      </div>
      <header className="page__head between">
        <div>
          <h1 className="page__title">{proj.name}</h1>
          <p className="page__sub">
            {proj.code && <span className="mono">{proj.code} · </span>}
            {proj.clientName || "—"} · {human(proj.sector)}
          </p>
        </div>
        <div className="head-actions">
          <Link className="ax-btn ax-btn--primary ax-btn--sm" to={`/projects/${id}/supervision`}>
            {t("sup.title")}
          </Link>
          <Link className="ax-btn ax-btn--outline ax-btn--sm" to={`/projects/${id}/registers`}>
            {t("reg.title")}
          </Link>
          <Link className="ax-btn ax-btn--outline ax-btn--sm" to={`/projects/${id}/logs`}>
            {t("log.title")}
          </Link>
          <Link className="ax-btn ax-btn--outline ax-btn--sm" to={`/projects/${id}/forms`}>
            {t("forms.open")}
          </Link>
          <Badge tone={statusTone(proj.status)}>{human(proj.status)}</Badge>
        </div>
      </header>

      <div className="detail-grid">
        <Card title={t("projects.overview")}>
          <Row k={t("projects.client")} v={proj.clientName || "—"} />
          <Row k={t("projects.location")} v={proj.location || "—"} />
          <Row k={t("projects.startDate")} v={proj.startDate || "—"} />
          <Row k={t("projects.targetDate")} v={proj.targetDate || "—"} />
          {proj.description && <p className="proj-desc">{proj.description}</p>}
        </Card>

        <Card title={t("projects.phases")}>
          <ol className="phases">
            {proj.phases.map((ph) => (
              <li key={ph.id} className={`phase phase--${ph.status}`}>
                <span className="phase__dot" />
                <span className="phase__name">{ph.name}</span>
                {canEdit ? (
                  <button className={`phase__btn is-${ph.status}`} onClick={() => void cyclePhase(ph)}>
                    {t(`phase.${ph.status}`)}
                  </button>
                ) : (
                  <Badge tone={ph.status === "done" ? "good" : ph.status === "active" ? "accent" : "neutral"}>
                    {t(`phase.${ph.status}`)}
                  </Badge>
                )}
              </li>
            ))}
          </ol>
        </Card>

        <Card title={t("projects.stakeholders")} className="grid__wide">
          <div className="tab-toolbar">
            <p className="page__sub">{t("projects.stakeholdersHint")}</p>
            {canEdit && (
              <Button size="sm" onClick={() => setAddOpen(true)}>
                {t("projects.addStakeholder")}
              </Button>
            )}
          </div>
          {proj.stakeholders.length === 0 ? (
            <p className="muted">{t("projects.noStakeholders")}</p>
          ) : (
            <div className="stake-list">
              {proj.stakeholders.map((s) => (
                <div className="stake" key={s.id}>
                  <Badge tone="accent">{stypeLabel(catalog, s.stype)}</Badge>
                  <div className="stake__name">
                    {s.name}
                    {s.org && <span className="muted"> · {s.org}</span>}
                  </div>
                  <div className="stake__mail mono">{s.email || "—"}</div>
                  {canEdit && (
                    <button className="rec__del" onClick={() => void removeStake(s)} aria-label="Remove">
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card title={t("group.title")} className="grid__wide">
          <div className="tab-toolbar">
            <p className="page__sub">{t("group.hint")}</p>
            {canManageTeam && (
              <Button size="sm" onClick={() => setGroupDialog({ mode: "add" })}>
                {t("group.add")}
              </Button>
            )}
          </div>
          {groups.length === 0 ? (
            <p className="muted">{t("group.empty")}</p>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>{t("group.name")}</th>
                    <th>{t("team.role")}</th>
                    <th>{t("team.abilities")}</th>
                    <th>{t("group.members")}</th>
                    {canManageTeam && <th aria-label="actions" />}
                  </tr>
                </thead>
                <tbody>
                  {groups.map((g) => (
                    <tr key={g.id}>
                      <td>
                        <div className="u-name">{g.name}</div>
                      </td>
                      <td>
                        <Badge tone="accent">{roleLabel(catalog, g.role)}</Badge>
                      </td>
                      <td>
                        <div className="chips">
                          {g.abilities.map((a) => (
                            <Badge key={a}>{abilityLabel(catalog, a)}</Badge>
                          ))}
                        </div>
                      </td>
                      <td className="mono">{g.memberCount}</td>
                      {canManageTeam && (
                        <td className="right">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setGroupDialog({ mode: "edit", target: g })}
                          >
                            {t("common.edit")}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => void removeGroup(g)}>
                            {t("common.delete")}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card title={t("team.title")} className="grid__wide">
          <div className="tab-toolbar">
            <p className="page__sub">{t("team.hint")}</p>
            {canManageTeam && (
              <Button size="sm" onClick={() => setMemberDialog({ mode: "add" })}>
                {t("team.add")}
              </Button>
            )}
          </div>
          {members.length === 0 ? (
            <p className="muted">{t("team.empty")}</p>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>{t("users.name")}</th>
                    <th>{t("group.one")}</th>
                    <th>{t("team.role")}</th>
                    <th>{t("team.abilities")}</th>
                    {canManageTeam && <th aria-label="actions" />}
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <tr key={m.id}>
                      <td>
                        <div className="u-name">{m.name}</div>
                        <div className="u-mail mono">{m.email}</div>
                      </td>
                      <td>
                        {m.groupId ? (
                          <Badge tone="good">{m.groupName}</Badge>
                        ) : (
                          <span className="muted">{t("group.individual")}</span>
                        )}
                      </td>
                      <td>
                        <Badge tone="accent">{roleLabel(catalog, m.role)}</Badge>
                      </td>
                      <td>
                        <div className="chips">
                          {m.abilities.map((a) => (
                            <Badge key={a}>{abilityLabel(catalog, a)}</Badge>
                          ))}
                        </div>
                      </td>
                      {canManageTeam && (
                        <td className="right">
                          <Button size="sm" variant="ghost" onClick={() => setMemberDialog({ mode: "edit", target: m })}>
                            {t("common.edit")}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => void removeMember(m)}>
                            {t("common.delete")}
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {addOpen && (
        <StakeholderModal projectId={id} catalog={catalog} onClose={() => setAddOpen(false)} onAdded={onStakeAdded} />
      )}
      {memberDialog && (
        <MemberModal
          projectId={id}
          catalog={catalog}
          groups={groups}
          mode={memberDialog.mode}
          target={memberDialog.target}
          onClose={() => setMemberDialog(null)}
          onSaved={onMemberSaved}
        />
      )}
      {groupDialog && (
        <GroupModal
          projectId={id}
          catalog={catalog}
          mode={groupDialog.mode}
          target={groupDialog.target}
          onClose={() => setGroupDialog(null)}
          onSaved={onGroupSaved}
        />
      )}
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="kv">
      <span className="kv__k">{k}</span>
      <span className="kv__v">{v}</span>
    </div>
  );
}

function stypeLabel(c: Catalog, key: string): string {
  return c.stakeholderTypes.find((s) => s.key === key)?.label ?? key;
}
function roleLabel(c: Catalog, key: string): string {
  return c.projectMemberRoles.find((r) => r.key === key)?.label ?? key;
}
function abilityLabel(c: Catalog, key: string): string {
  return c.projectAbilities.find((a) => a.key === key)?.label ?? key;
}

function MemberModal({
  projectId,
  catalog,
  groups,
  mode,
  target,
  onClose,
  onSaved,
}: {
  projectId: string;
  catalog: Catalog;
  groups: ProjectGroup[];
  mode: "add" | "edit";
  target?: ProjectMember;
  onClose: () => void;
  onSaved: (m: ProjectMember) => void;
}) {
  const { t } = useTranslation();
  const roles = catalog.projectMemberRoles;
  const [source, setSource] = useState<"existing" | "new">("existing");
  const [users, setUsers] = useState<UserListItem[] | null>(null);
  const [userId, setUserId] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [groupId, setGroupId] = useState(target?.groupId ?? "");
  const [role, setRole] = useState(target?.role ?? roles[0]?.key ?? "viewer");
  const [abilities, setAbilities] = useState<string[]>(
    target?.abilities ?? roles[0]?.abilities ?? ["read"],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // In a group the role + abilities come from the group, so they're read-only here.
  const inGroup = Boolean(groupId);
  const chosenGroup = groups.find((g) => g.id === groupId);

  useEffect(() => {
    if (mode !== "add") return;
    endpoints
      .listUsers()
      .then((u) => {
        setUsers(u);
        if (u[0]) setUserId(u[0].id);
      })
      .catch(() => {
        setUsers([]);
        setSource("new"); // can't list users → only new-credential
      });
  }, [mode]);

  function chooseRole(r: string) {
    setRole(r);
    setAbilities(roles.find((x) => x.key === r)?.abilities ?? ["read"]);
  }
  function toggleAbility(a: string) {
    setAbilities((s) => (s.includes(a) ? s.filter((x) => x !== a) : [...s, a]));
  }

  async function submit() {
    setBusy(true);
    setError("");
    // A group carries its own role + abilities; sending "" un-groups the member.
    const access = inGroup ? { groupId } : { groupId: "", role, abilities };
    try {
      if (mode === "edit" && target) {
        onSaved(await endpoints.updateMember(projectId, target.id, access));
      } else if (source === "existing") {
        onSaved(await endpoints.addMember(projectId, { userId, ...access }));
      } else {
        onSaved(await endpoints.addMember(projectId, { email, name, password, ...access }));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={mode === "edit" ? t("team.edit") : t("team.add")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      {error && <div className="form-err">{error}</div>}

      {mode === "add" && (
        <>
          <div className="segmented">
            <button
              type="button"
              className={source === "existing" ? "seg is-on" : "seg"}
              onClick={() => setSource("existing")}
            >
              {t("team.existing")}
            </button>
            <button
              type="button"
              className={source === "new" ? "seg is-on" : "seg"}
              onClick={() => setSource("new")}
            >
              {t("team.newCred")}
            </button>
          </div>

          {source === "existing" ? (
            <Field label={t("team.pickUser")}>
              <Select value={userId} onChange={(e) => setUserId(e.target.value)}>
                {(users ?? []).map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} · {u.email}
                  </option>
                ))}
              </Select>
            </Field>
          ) : (
            <>
              <Field label={t("users.name")}>
                <Input value={name} onChange={(e) => setName(e.target.value)} />
              </Field>
              <Field label={t("login.email")}>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </Field>
              <Field label={t("login.password")} hint={t("users.pwHint")}>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </Field>
            </>
          )}
        </>
      )}

      <Field label={t("group.one")} hint={t("group.pickHint")}>
        <Select value={groupId} onChange={(e) => setGroupId(e.target.value)}>
          <option value="">{t("group.individual")}</option>
          {groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}
            </option>
          ))}
        </Select>
      </Field>

      {inGroup ? (
        <div className="group-inherit">
          <Badge tone="accent">{roleLabel(catalog, chosenGroup?.role ?? "")}</Badge>
          <div className="chips">
            {(chosenGroup?.abilities ?? []).map((a) => (
              <Badge key={a}>{abilityLabel(catalog, a)}</Badge>
            ))}
          </div>
          <p className="muted">{t("group.inherits")}</p>
        </div>
      ) : (
        <>
          <Field label={t("team.role")}>
            <Select value={role} onChange={(e) => chooseRole(e.target.value)}>
              {roles.map((r) => (
                <option key={r.key} value={r.key}>
                  {r.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t("team.abilities")} hint={t("team.abilitiesHint")}>
            <div className="chips">
              {catalog.projectAbilities.map((a) => (
                <button
                  type="button"
                  key={a.key}
                  className={abilities.includes(a.key) ? "chip is-on" : "chip"}
                  onClick={() => toggleAbility(a.key)}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </Field>
        </>
      )}
    </Modal>
  );
}

function GroupModal({
  projectId,
  catalog,
  mode,
  target,
  onClose,
  onSaved,
}: {
  projectId: string;
  catalog: Catalog;
  mode: "add" | "edit";
  target?: ProjectGroup;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const roles = catalog.projectMemberRoles;
  const [name, setName] = useState(target?.name ?? "");
  const [role, setRole] = useState(target?.role ?? roles[0]?.key ?? "viewer");
  const [abilities, setAbilities] = useState<string[]>(
    target?.abilities ?? roles[0]?.abilities ?? ["read"],
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  function chooseRole(r: string) {
    setRole(r);
    setAbilities(roles.find((x) => x.key === r)?.abilities ?? ["read"]);
  }
  function toggleAbility(a: string) {
    setAbilities((s) => (s.includes(a) ? s.filter((x) => x !== a) : [...s, a]));
  }

  async function submit() {
    setBusy(true);
    setError("");
    try {
      if (mode === "edit" && target) {
        await endpoints.updateGroup(projectId, target.id, { name, role, abilities });
      } else {
        await endpoints.createGroup(projectId, { name, role, abilities });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={mode === "edit" ? t("group.edit") : t("group.add")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      {error && <div className="form-err">{error}</div>}
      <Field label={t("group.name")} hint={t("group.nameHint")}>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label={t("team.role")}>
        <Select value={role} onChange={(e) => chooseRole(e.target.value)}>
          {roles.map((r) => (
            <option key={r.key} value={r.key}>
              {r.label}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t("team.abilities")} hint={t("group.abilitiesHint")}>
        <div className="chips">
          {catalog.projectAbilities.map((a) => (
            <button
              type="button"
              key={a.key}
              className={abilities.includes(a.key) ? "chip is-on" : "chip"}
              onClick={() => toggleAbility(a.key)}
            >
              {a.label}
            </button>
          ))}
        </div>
      </Field>
    </Modal>
  );
}

function StakeholderModal({
  projectId,
  catalog,
  onClose,
  onAdded,
}: {
  projectId: string;
  catalog: Catalog;
  onClose: () => void;
  onAdded: (s: Stakeholder) => void;
}) {
  const { t } = useTranslation();
  const [stype, setStype] = useState(catalog.stakeholderTypes[0]?.key ?? "other");
  const [name, setName] = useState("");
  const [org, setOrg] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    setBusy(true);
    setError("");
    try {
      onAdded(await endpoints.addStakeholder(projectId, { stype, name, org, email }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={t("projects.addStakeholder")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {t("common.create")}
          </Button>
        </>
      }
    >
      {error && <div className="form-err">{error}</div>}
      <Field label={t("projects.stakeholderType")}>
        <Select value={stype} onChange={(e) => setStype(e.target.value)}>
          {catalog.stakeholderTypes.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </Select>
      </Field>
      <Field label={t("users.name")}>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label={t("projects.org")}>
        <Input value={org} onChange={(e) => setOrg(e.target.value)} />
      </Field>
      <Field label={t("login.email")}>
        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>
    </Modal>
  );
}
